import React, { useState } from 'react';

import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Divider,
  Button,
  Stack,
  Popover,
  Typography,
  FormHelperText,
  Box,
  useMediaQuery,
} from '@mui/material';
import Iconify from '../iconify/iconify';
import useShortcutItems from './use-shortcut-items';
import RollUpTabs from './rollup-tabs';
import ShortcutRow from './shortcut-row';

export default function RollupPeriodPicker({
  startDate,
  endDate,
  onChangeStartDate,
  onChangeEndDate,
  rollup,
  setRollup,
  error,
  sx,
}) {
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const { shortcutsItems, shortcutItem, setShortcutItem } = useShortcutItems();

  const handleCalendarClick = (event) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const calendarPopoverOpen = Boolean(calendarAnchorEl);
  const isSmallScreen = useMediaQuery('(max-width:550px)');

  const adjustStartDate = (direction) => {
    setShortcutItem(null);
    const unit = rollup === 'monthly' ? 'month' : rollup === 'quarterly' ? 'quarter' : 'year';
    const adjustment = direction === 'forward' ? 1 : -1;
    onChangeStartDate(moment(startDate).add(adjustment, unit).toDate());
  };

  const adjustEndDate = (direction) => {
    setShortcutItem(null);
    const unit = rollup === 'monthly' ? 'month' : rollup === 'quarterly' ? 'quarter' : 'year';
    const adjustment = direction === 'forward' ? 1 : -1;
    onChangeEndDate(moment(endDate).add(adjustment, unit).toDate());
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        sx={{ minWidth: '25px' }}
        onClick={handleCalendarClick}
        startIcon={<Iconify icon="mdi:calendar-month" />}
      >
        {!isSmallScreen && (
          <Typography variant="body2" fontWeight="bold">
            {shortcutItem?.primary ??
              `${moment(startDate)?.format('MMM YYYY')} - ${moment(endDate)?.format('MMM YYYY')}`}
          </Typography>
        )}
      </Button>
      <Popover
        open={calendarPopoverOpen}
        anchorEl={calendarAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleCalendarClose}
      >
        <Stack minWidth={200} alignItems="center">
          <Box sx={{ px: 1 }}>
            <RollUpTabs value={rollup} onChange={setRollup} />
          </Box>
          <Divider />
          {shortcutsItems.map((shortcut) => (
            <ShortcutRow
              key={shortcut.primary}
              shortcut={shortcut}
              onClick={() => {
                setShortcutItem(shortcut);
                const newValue = shortcut.getValue();
                if (newValue[0]) onChangeStartDate(newValue[0]);
                if (newValue[1]) onChangeEndDate(newValue[1]);
                handleCalendarClose();
              }}
            />
          ))}
          <Divider />
          <Stack direction="row" justifyContent="space-between" sx={{ p: 1 }} spacing={1}>
            <Button
              variant="outlined"
              onClick={() => adjustStartDate('backward')}
              startIcon={<Iconify icon="mdi:rewind" />}
            >
              Shift Start
            </Button>
            <Button
              variant="outlined"
              onClick={() => adjustEndDate('forward')}
              endIcon={<Iconify icon="mdi:fast-forward" />}
            >
              Shift End
            </Button>
          </Stack>
        </Stack>
      </Popover>

      {error && (
        <FormHelperText error sx={{ px: 2 }}>
          End date must be later than the start date
        </FormHelperText>
      )}
    </>
  );
}

RollupPeriodPicker.propTypes = {
  error: PropTypes.bool,
  onChangeEndDate: PropTypes.func,
  onChangeStartDate: PropTypes.func,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
};
